import type { ScreenshotTemplate } from "./ScreenshotTemplates.tsx";
import type { DeviceSpec } from "./devices.ts";

export interface RenderOptions {
  device: DeviceSpec;
  template: ScreenshotTemplate;
  headline: string;
  subtitle: string;
  screenshotImage: HTMLImageElement | null;
  scale?: number;
}

/**
 * Device frame constants (proportional to device size).
 * The frame takes up the lower ~65% of the canvas, leaving the top
 * ~35% for headline + subtitle text.
 */
function getFrameMetrics(device: DeviceSpec, scale: number) {
  const w = device.width * scale;
  const h = device.height * scale;

  const isWatch = device.category === "watch";
  const isIpad = device.category === "ipad";

  // How much of the canvas height the device frame occupies
  const frameRatio = isWatch ? 0.6 : isIpad ? 0.55 : 0.62;
  const frameHeight = h * frameRatio;
  const frameWidth = frameHeight * (device.width / device.height);

  // Bezel thickness
  const bezel = isWatch ? 8 * scale : isIpad ? 12 * scale : 10 * scale;

  // Corner radius for the device frame
  const cornerRadius = isWatch ? frameWidth * 0.28 : isIpad ? 18 * scale : 40 * scale;

  // Position: centered horizontally, anchored toward bottom
  const x = (w - frameWidth) / 2;
  const y = h - frameHeight - h * 0.06;

  return { frameWidth, frameHeight, bezel, cornerRadius, x, y, canvasWidth: w, canvasHeight: h };
}

function parseGradient(gradientStr: string, ctx: CanvasRenderingContext2D, w: number, h: number) {
  // Parse CSS linear-gradient string like "linear-gradient(160deg, #ff6b00, #ff9500)"
  const match = gradientStr.match(
    /linear-gradient\(\s*(\d+)deg\s*,\s*(#[0-9a-fA-F]{6})\s*,\s*(#[0-9a-fA-F]{6})\s*\)/,
  );
  if (!match) {
    // Fallback: solid dark
    ctx.fillStyle = "#1a1a2e";
    return;
  }

  const angle = (Number.parseInt(match[1], 10) * Math.PI) / 180;
  const color1 = match[2];
  const color2 = match[3];

  // Calculate gradient start/end based on angle
  const cx = w / 2;
  const cy = h / 2;
  const len = Math.sqrt(w * w + h * h) / 2;
  const x0 = cx - Math.cos(angle) * len;
  const y0 = cy - Math.sin(angle) * len;
  const x1 = cx + Math.cos(angle) * len;
  const y1 = cy + Math.sin(angle) * len;

  const grad = ctx.createLinearGradient(x0, y0, x1, y1);
  grad.addColorStop(0, color1);
  grad.addColorStop(1, color2);
  ctx.fillStyle = grad;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function drawDeviceFrame(
  ctx: CanvasRenderingContext2D,
  metrics: ReturnType<typeof getFrameMetrics>,
  screenshotImage: HTMLImageElement | null,
) {
  const { frameWidth, frameHeight, bezel, cornerRadius, x, y } = metrics;

  // Outer bezel
  ctx.fillStyle = "#1a1a1a";
  roundRect(ctx, x, y, frameWidth, frameHeight, cornerRadius);
  ctx.fill();

  // Inner bezel highlight
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  roundRect(ctx, x, y, frameWidth, frameHeight, cornerRadius);
  ctx.stroke();

  // Screen area
  const screenX = x + bezel;
  const screenY = y + bezel;
  const screenW = frameWidth - bezel * 2;
  const screenH = frameHeight - bezel * 2;
  const innerRadius = Math.max(cornerRadius - bezel, 4);

  ctx.save();
  roundRect(ctx, screenX, screenY, screenW, screenH, innerRadius);
  ctx.clip();

  if (screenshotImage) {
    // Draw image to fill screen area, maintaining aspect ratio (cover)
    const imgAspect = screenshotImage.width / screenshotImage.height;
    const screenAspect = screenW / screenH;

    let drawW: number;
    let drawH: number;
    let drawX: number;
    let drawY: number;

    if (imgAspect > screenAspect) {
      drawH = screenH;
      drawW = screenH * imgAspect;
      drawX = screenX - (drawW - screenW) / 2;
      drawY = screenY;
    } else {
      drawW = screenW;
      drawH = screenW / imgAspect;
      drawX = screenX;
      drawY = screenY - (drawH - screenH) / 2;
    }
    ctx.drawImage(screenshotImage, drawX, drawY, drawW, drawH);
  } else {
    // Placeholder
    ctx.fillStyle = "#2a2a2a";
    ctx.fillRect(screenX, screenY, screenW, screenH);

    // Placeholder icon
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    ctx.font = `${Math.min(screenW, screenH) * 0.15}px -apple-system, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Screenshot", screenX + screenW / 2, screenY + screenH / 2);
  }

  ctx.restore();
}

function drawText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  fontSize: number,
  color: string,
  fontWeight: string,
  lineHeight: number,
) {
  ctx.fillStyle = color;
  ctx.font = `${fontWeight} ${fontSize}px -apple-system, "SF Pro Display", "Helvetica Neue", sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  const lines = text.split("\n");
  let currentY = y;
  for (const line of lines) {
    // Word wrap if needed
    const words = line.split(" ");
    let currentLine = "";
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        ctx.fillText(currentLine, x, currentY);
        currentLine = word;
        currentY += lineHeight;
      } else {
        currentLine = testLine;
      }
    }
    ctx.fillText(currentLine, x, currentY);
    currentY += lineHeight;
  }

  return currentY;
}

export function renderScreenshot(canvas: HTMLCanvasElement, options: RenderOptions): void {
  const { device, template, headline, subtitle, screenshotImage, scale = 1 } = options;

  const w = device.width * scale;
  const h = device.height * scale;

  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // 1. Draw gradient background
  parseGradient(template.gradient, ctx, w, h);
  ctx.fillRect(0, 0, w, h);

  // 2. Calculate text sizing
  const isWatch = device.category === "watch";
  const isIpad = device.category === "ipad";

  const headlineFontSize = isWatch ? 28 * scale : isIpad ? 100 * scale : 80 * scale;
  const subtitleFontSize = isWatch ? 14 * scale : isIpad ? 44 * scale : 36 * scale;
  const textPaddingX = w * 0.08;
  const textStartY = isWatch ? h * 0.06 : h * 0.06;

  // 3. Draw headline
  const headlineBottom = drawText(
    ctx,
    headline,
    w / 2,
    textStartY,
    w - textPaddingX * 2,
    headlineFontSize,
    template.textColor,
    "700",
    headlineFontSize * 1.15,
  );

  // 4. Draw subtitle
  const subtitleGap = isWatch ? 6 * scale : 16 * scale;
  drawText(
    ctx,
    subtitle,
    w / 2,
    headlineBottom + subtitleGap,
    w - textPaddingX * 2,
    subtitleFontSize,
    `${template.textColor}cc`,
    "400",
    subtitleFontSize * 1.4,
  );

  // 5. Draw device frame with screenshot
  const metrics = getFrameMetrics(device, scale);
  drawDeviceFrame(ctx, metrics, screenshotImage);
}

export function exportScreenshot(
  device: DeviceSpec,
  template: ScreenshotTemplate,
  headline: string,
  subtitle: string,
  screenshotImage: HTMLImageElement | null,
): void {
  const offscreen = document.createElement("canvas");
  renderScreenshot(offscreen, {
    device,
    template,
    headline,
    subtitle,
    screenshotImage,
    scale: 1,
  });

  offscreen.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `screenshot-${device.id}.png`;
    a.click();
    URL.revokeObjectURL(url);
  }, "image/png");
}

export function batchExport(
  devices: DeviceSpec[],
  template: ScreenshotTemplate,
  headline: string,
  subtitle: string,
  screenshotImage: HTMLImageElement | null,
): void {
  const requiredDevices = devices.filter((d) => d.required);
  for (const device of requiredDevices) {
    exportScreenshot(device, template, headline, subtitle, screenshotImage);
  }
}
